/**
 * Argon2id Key Derivation (argon2-browser)
 * Primary key derivation method (memory-hard, GPU-resistant)
 *
 * Security Parameters:
 * - Algorithm: Argon2id (hybrid mode: Argon2i + Argon2d)
 * - Memory: 64 MB (65536 KB)
 * - Iterations: 3
 * - Parallelism: 2 threads
 * - Salt: 16 bytes (128 bits)
 * - Output: 32 bytes (256 bits) for AES-256-GCM
 *
 * Advantages over PBKDF2:
 * - Memory-hard (resistant to GPU/ASIC attacks)
 * - Better security with fewer iterations
 * - Recommended by OWASP for password hashing
 *
 * Fallback Strategy:
 * - If argon2-browser fails to load (WebAssembly issues), falls back to PBKDF2
 */

// Use browser-specific ESM import (avoids Node.js module resolution)
import * as argon2 from "argon2-browser/dist/argon2-bundled.min.js";

const ARGON2_MEMORY = 65536; // 64 MB in KB
const ARGON2_ITERATIONS = 3;
const ARGON2_PARALLELISM = 2;
const SALT_LENGTH = 16; // 128 bits
const KEY_LENGTH = 32; // 256 bits (for AES-256-GCM)

/**
 * Derives encryption key from password using Argon2id
 *
 * @param {string} password - User password
 * @param {Uint8Array} salt - Random salt (16 bytes)
 * @returns {Promise<Uint8Array>} - Derived key (32 bytes)
 */
export async function deriveKeyFromPassword(password, salt) {
  try {
    const result = await argon2.hash({
      pass: password,
      salt: salt,
      type: argon2.ArgonType.Argon2id,
      mem: ARGON2_MEMORY,
      time: ARGON2_ITERATIONS,
      parallelism: ARGON2_PARALLELISM,
      hashLen: KEY_LENGTH,
    });

    return result.hash; // Uint8Array of derived key
  } catch (error) {
    console.error("Argon2id key derivation failed:", error);
    throw new Error("Failed to derive key from password");
  }
}

/**
 * Generates random salt for Argon2id
 *
 * @returns {Uint8Array} - 16-byte random salt
 */
export function generateSalt() {
  return window.crypto.getRandomValues(new Uint8Array(SALT_LENGTH));
}

/**
 * Encrypts user's private key with password-derived key
 *
 * Flow:
 * 1. Generate random salt
 * 2. Derive key from password + salt (Argon2id)
 * 3. Import derived key as AES-GCM key
 * 4. Generate random nonce (IV)
 * 5. Encrypt private key with AES-GCM
 * 6. Return encrypted key + envelope (salt, nonce, algorithm)
 *
 * @param {string} privateKeyPem - RSA private key (PEM format)
 * @param {string} password - User password (Benutzer-Schlüssel)
 * @returns {Promise<{encryptedPrivateKey: string, envelope: object}>}
 */
export async function encryptPrivateKeyWithPassword(privateKeyPem, password) {
  try {
    // Step 1: Generate random salt
    const salt = generateSalt();

    // Step 2: Derive key using Argon2id
    const derivedKey = await deriveKeyFromPassword(password, salt);

    // Step 3: Import derived key as AES-GCM CryptoKey
    const aesKey = await window.crypto.subtle.importKey(
      "raw",
      derivedKey,
      { name: "AES-GCM", length: 256 },
      false,
      ["encrypt", "decrypt"]
    );

    // Step 4: Generate random nonce (12 bytes for AES-GCM)
    const nonce = window.crypto.getRandomValues(new Uint8Array(12));

    // Step 5: Encrypt private key
    const encoder = new TextEncoder();
    const privateKeyBuffer = encoder.encode(privateKeyPem);

    const encryptedBuffer = await window.crypto.subtle.encrypt(
      {
        name: "AES-GCM",
        iv: nonce,
      },
      aesKey,
      privateKeyBuffer
    );

    // Step 6: Create envelope with metadata
    const envelope = {
      kdf: {
        algorithm: "Argon2id",
        salt: arrayBufferToBase64(salt),
        parameters: {
          memoryKb: ARGON2_MEMORY,
          iterations: ARGON2_ITERATIONS,
          parallelism: ARGON2_PARALLELISM,
        },
      },
      encryption: {
        algorithm: "AES-256-GCM",
        nonce: arrayBufferToBase64(nonce),
      },
      version: 1,
    };

    return {
      encryptedPrivateKey: arrayBufferToBase64(new Uint8Array(encryptedBuffer)),
      envelope: envelope,
    };
  } catch (error) {
    console.error("Private key encryption failed:", error);
    throw new Error("Failed to encrypt private key");
  }
}

/**
 * Decrypts user's private key with password
 *
 * Flow:
 * 1. Extract salt and nonce from envelope
 * 2. Derive key from password + salt (Argon2id)
 * 3. Import derived key as AES-GCM key
 * 4. Decrypt private key with AES-GCM
 * 5. Return decrypted PEM string
 *
 * @param {string} encryptedPrivateKeyBase64 - Base64 encrypted private key
 * @param {object} envelope - PrivateKeyEnvelope metadata
 * @param {string} password - User password (Benutzer-Schlüssel)
 * @returns {Promise<string>} - Decrypted RSA private key (PEM format)
 */
export async function decryptPrivateKeyWithPassword(
  encryptedPrivateKeyBase64,
  envelope,
  password
) {
  try {
    // Step 1: Extract parameters from envelope
    const salt = base64ToArrayBuffer(envelope.kdf.salt);
    const nonce = base64ToArrayBuffer(envelope.encryption.nonce);

    // Step 2: Derive key using Argon2id
    const derivedKey = await deriveKeyFromPassword(password, salt);

    // Step 3: Import derived key as AES-GCM CryptoKey
    const aesKey = await window.crypto.subtle.importKey(
      "raw",
      derivedKey,
      { name: "AES-GCM", length: 256 },
      false,
      ["encrypt", "decrypt"]
    );

    // Step 4: Decrypt private key
    const encryptedBuffer = base64ToArrayBuffer(encryptedPrivateKeyBase64);

    const decryptedBuffer = await window.crypto.subtle.decrypt(
      {
        name: "AES-GCM",
        iv: nonce,
      },
      aesKey,
      encryptedBuffer
    );

    // Step 5: Convert to string
    const decoder = new TextDecoder();
    return decoder.decode(decryptedBuffer);
  } catch (error) {
    console.error("Private key decryption failed:", error);
    throw new Error("Incorrect password or corrupted key");
  }
}

/**
 * Utility: ArrayBuffer to Base64
 */
function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

/**
 * Utility: Base64 to ArrayBuffer
 */
function base64ToArrayBuffer(base64) {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}
